import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../Config";
import SpinLoader from "../loader/SpinLoader";
import PropTypes from "prop-types";

const ViewExpense = ({ show, expense, orgId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [parent, setParent] = useState(null);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (!show) {
      setParent(null);
      setPayments([]); // we'll actually not need separate parent/payments anymore
      setErrorMsg("");
      setLoading(false);
      return;
    }

    if (!expense || !expense.Id) {
      setErrorMsg("No expense selected");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}Misc/getExpenseById?OrgId=${orgId}&Id=${expense.Id}`,
        );

        if (res.data?.Status && Array.isArray(res.data.ResultData)) {
          const records = res.data.ResultData;

          const sortedRecords = [...records].sort((a, b) => {
            const dateA = new Date(a.PaidDate || a.DateOfTransaction || 0);
            const dateB = new Date(b.PaidDate || b.DateOfTransaction || 0);
            return dateA - dateB; // oldest first
          });

          setPayments(sortedRecords); // rename or repurpose state — now holds everything

          const parentRecord =
            records.find((r) => r.RowType === "Parent" || r.RefId === null) ||
            records[0];
          setParent(parentRecord);
        } else {
          setErrorMsg("No expense details found");
          setPayments([]);
          setParent(expense); // fallback
        }
      } catch (err) {
        console.error("Fetch failed:", err);
        setErrorMsg("Failed to load expense details");
        setPayments([]);
        setParent(expense);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [show, expense, orgId]);

  if (!show) return null;

  return (
    <>
      {loading && <SpinLoader />}

      <style>{`
        .offcanvas { transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1); visibility: hidden; }
        .offcanvas.show { visibility: visible; }
        .offcanvas-backdrop { opacity: 0; transition: opacity 0.3s ease; }
        .offcanvas-backdrop.show { opacity: 0.5; }
        .form-control:disabled, .form-control[readonly] { 
          background-color: #f8f9fa; 
          cursor: not-allowed; 
        }
        .note-icon { 
          font-size: 1.5rem; 
          cursor: help; 
        }
        @media (max-width: 576px)    { #offcanvasViewExpense { width: 90% !important; } }
        @media (min-width: 577px) and (max-width: 992px)  { #offcanvasViewExpense { width: 60% !important; } }
        @media (min-width: 993px) { #offcanvasViewExpense { width: 50% !important; } }
      `}</style>

      <div
        className={`offcanvas-backdrop fade ${show ? "show" : ""}`}
        style={{ pointerEvents: show ? "auto" : "none", zIndex: 1040 }}
        onClick={onClose}
      />

      <div
        className={`offcanvas offcanvas-end ${show ? "show" : ""}`}
        tabIndex="-1"
        id="offcanvasViewExpense"
        aria-labelledby="offcanvasViewExpenseLabel"
        style={{ pointerEvents: show ? "auto" : "none" }}
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="offcanvasViewExpenseLabel">
            View Expense - {parent?.ReferenceNo || "N/A"}
          </h5>
          <button
            type="button"
            className="btn-close text-reset"
            onClick={onClose}
            aria-label="Close"
          />
        </div>
        <hr className="m-0 p-0" />

        <div className="offcanvas-body">
          {errorMsg && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMsg}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMsg("")}
              />
            </div>
          )}

          {loading ? (
            <div className="text-center py-5">
              <SpinLoader />
              <p>Loading expense details...</p>
            </div>
          ) : parent ? (
            <>
              {/* Parent / Header Info - same style as EditExpense */}
              <div className="mb-4">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Store</label>
                    <input
                      type="text"
                      className="form-control"
                      value={parent.StoreName || "N/A"}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Supplier</label>
                    <input
                      type="text"
                      className="form-control"
                      value={parent.SupplierName || "N/A"}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Expense Type</label>
                    <input
                      type="text"
                      className="form-control"
                      value={parent.ExpenseTypeName || "N/A"}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Bill Date</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        parent.DateOfTransaction
                          ? new Date(
                              parent.DateOfTransaction,
                            ).toLocaleDateString("en-GB")
                          : "N/A"
                      }
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Total Amount</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`₹${Number(parent.TotalAmount || parent.Amount || 0).toFixed(2)}`}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="col-md-4 ">
                    <label className="form-label fw-bold">Due Amount</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`₹${Number(expense?.DueAmount || 0).toFixed(2)}`}
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="col-md-4 d-none">
                    <label className="form-label fw-bold">Status</label>
                    <input
                      type="text"
                      className="form-control"
                      value={parent.Status === 1 ? "Completed" : "Pending"}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>

              {/* Payment History - same style as updated EditExpense */}
              {/* Payment History - now shows ALL records (parent + payments) */}
              <div className="card mt-4">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    Transaction History ({payments.length} entries)
                  </h6>
                  {payments.length > 0 && (
                    <small className="text-muted">
                      Last entry:{" "}
                      {new Date(
                        payments[payments.length - 1]?.PaidDate ||
                          payments[payments.length - 1]?.DateOfTransaction,
                      ).toLocaleDateString("en-GB")}
                    </small>
                  )}
                </div>

                <div className="card-body p-0">
                  {payments.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No transactions recorded.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Amount</th>
                            <th>Date</th>

                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((record, index) => (
                            <tr key={record.Id}>
                              <td>{index + 1}</td>

                              <td>
                                ₹
                                {Number(
                                  record.PaidAmount || record.Amount || 0,
                                ).toFixed(2)}
                              </td>
                              <td>
                                {record.PaidDate || record.DateOfTransaction
                                  ? new Date(
                                      record.PaidDate ||
                                        record.DateOfTransaction,
                                    ).toLocaleDateString("en-GB")
                                  : "-"}
                              </td>

                              <td>
                                {record.Note ? (
                                  <span title={record.Note}>
                                    <i className="fa-regular fa-message text-primary"></i>
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5 text-muted">
              No expense details available.
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end p-3">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};

ViewExpense.propTypes = {
  show: PropTypes.bool.isRequired,
  expense: PropTypes.object.isRequired,
  orgId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ViewExpense;
